import React, { useEffect, useMemo, useState } from 'react';
import { Switch, Route, useRouteMatch, useParams, useHistory } from 'react-router-dom';
import { RootState } from 'StoreModel';
import { connect } from 'react-redux';
import NotFound from '../404/NotFound';
import { Helmet } from 'react-helmet';
import {
  fetchApplicationDefinitionByIdAsync,
  fetchApplicationDefinitionOverviewByIdAsync,
  fetchApplicationDefinitionPreviewByidAsync,
  fetchCurrentUserApplicationsAsync,
} from '../../redux/actions/application-manager';
import { ApplicationDefinition, ApplicationEntry, Community } from 'redux/types/account';
import NewApplication from './new-application';
import EditApplication from './edit-application';
import PreviewApplication from './preview';
import { ProtectedRoute } from 'routes/navigation';
import { getLiteralsAsync } from 'redux/actions/account';
import { fetchCommunityDetailsAsync } from 'redux/actions/community';
import { DEFAULT_COMMUNITY } from 'redux/types/default-types-values';
import { fetchApplicationDefinitionById } from 'services/api/application-manager';
import { getLiterals } from 'services/literals';
import { ApplicationStatus, Flag } from 'redux/types/enums';
import { toast } from 'components/common/toast';

const mapStateToProps = (state: RootState) => ({
  bearer: state.account.session.session.bearer,
  literals: state.literals,
  applicationDefinitions: state.account.applicationDefinitions,
  account: state.account.details.user,
  selectedCommunity: state.account.selectedCommunity,
  userLanguage: state?.account?.details?.user?.language,
  isApplicationOverviewFetched: state.loading.fetchApplicationDefinitionOverviewByIdFlag,
  applications: state.account.applications,
});

const mapDispatchToProps = {
  fetchApplicationDefinition: fetchApplicationDefinitionByIdAsync.request,
  fetchApplicationDefinitionOverviewById: fetchApplicationDefinitionOverviewByIdAsync.request,
  getCommunityLiterals: getLiteralsAsync.request,
  fetchCommunityDetails: fetchCommunityDetailsAsync.request,
  fetchApplicationDefinitionPreview: fetchApplicationDefinitionPreviewByidAsync.request,
  fetchApplications: fetchCurrentUserApplicationsAsync.request,
};

type dispatchType = typeof mapDispatchToProps;
interface Props extends ReturnType<typeof mapStateToProps>, dispatchType {}

const ApplicationRouter: React.FC<Props> = props => {
  const {
    bearer,
    fetchApplicationDefinition,
    fetchApplicationDefinitionOverviewById,
    fetchApplicationDefinitionPreview,
    applicationDefinitions,
    account,
    getCommunityLiterals,
    fetchCommunityDetails,
    literals,
    selectedCommunity,
    userLanguage,
    isApplicationOverviewFetched,
    applications,
    fetchApplications,
  } = props;
  const { path }: { path: string; url: string } = useRouteMatch();
  const params = useParams<{ applicationDefinitionId: string }>();
  const [communityInfo, setCommunityInfo] = useState<Community>(DEFAULT_COMMUNITY);
  const history = useHistory();
  const applicationDefinitionId = Number(params.applicationDefinitionId);
  const applicationDefinition = applicationDefinitions.find(
    (applicationDefinition: ApplicationDefinition) => applicationDefinition.id === applicationDefinitionId,
  );
  const community: Community | null = useMemo(() => selectedCommunity, [
    applicationDefinition?.communityId,
    selectedCommunity,
  ]);

  useEffect(() => {
    if (bearer) {
      fetchApplications({
        bearer,
      });
    }
  }, [bearer]);

  // for community less users get and update literals and community info
  useEffect(() => {
    if (isApplicationOverviewFetched === Flag.Success)
      getLiterals(userLanguage || 'en-EN', applicationDefinition.communityId, bearer)
        .then(response => {
          setCommunityInfo({
            ...DEFAULT_COMMUNITY,
            isSDGsVisible: applicationDefinition?.isSDGsVisible,
            isDevelopmentStageVisible: applicationDefinition?.isDevelopmentStageVisible,
          });
        })
        .catch(() => toast(literals.apply_to_team_project_service_on_project_applt_to_team_error, { type: 'error' }));
  }, [isApplicationOverviewFetched]);

  useEffect(() => {
    // check if user is community less then update the application definition redux state accordingly
    if (bearer) {
      fetchApplicationDefinitionById(bearer, applicationDefinitionId)
        .then((response: { applicationDefinition: ApplicationDefinition }) => {
          fetchApplicationDefinitionOverviewById({ bearer, applicationDefinitionId });
        })
        .catch(() => toast(literals.apply_to_team_project_service_on_project_applt_to_team_error, { type: 'error' }));
    } else {
      fetchApplicationDefinitionPreview({ applicationDefinitionId });
    }
  }, []);

  useEffect(() => {
    if (!applicationDefinition?.communityId) return;
    if (!community) return;
    if (!community.customLiterals) {
      getCommunityLiterals({
        language: userLanguage || 'en-EN',
        communityId: community?.id || applicationDefinition.communityId,
        bearer,
      });
    }
  }, [applicationDefinition?.communityId, bearer, community?.id]);

  useEffect(() => {
    applicationDefinition?.communityId &&
      (!community || !('isLinkedinUrlVisible' in community)) &&
      bearer &&
      fetchCommunityDetails({ id: applicationDefinition?.communityId, bearer, silentCall: true });
  }, [community?.isLinkedinUrlVisible, applicationDefinition?.communityId]);

  useEffect(() => {
    if (bearer && applications?.length && applicationDefinition) {
      const acceptedOrSubmittedApplication = applications.find((application: ApplicationEntry) => {
        return (
          application.applicationDefinitionId === applicationDefinition.id &&
          (application.status === ApplicationStatus.Draft || application.status === ApplicationStatus.Apply)
        );
      });

      if (window.location.pathname.includes('/new-application') && acceptedOrSubmittedApplication) {
        history.push(
          `/application-definition/${applicationDefinition.id}/application/${acceptedOrSubmittedApplication.id}`,
        );
      }
    }
  }, [bearer, applications, applicationDefinition]);

  if (!applicationDefinition) return null;
  return (
    <>
      <Helmet>
        <title>{applicationDefinition ? applicationDefinition.name + ' - ' : ''} Application</title>
      </Helmet>
      <Switch>
        <ProtectedRoute exact={true} bearer={bearer} account={account} path={`${path}/new-application`}>
          <NewApplication community={community || communityInfo} applicationDefinition={applicationDefinition} />
        </ProtectedRoute>
        <ProtectedRoute exact={true} bearer={bearer} account={account} path={`${path}/application/:applicationEntryId`}>
          <EditApplication community={community || communityInfo} applicationDefinition={applicationDefinition} />
        </ProtectedRoute>
        <Route path={`${path}`}>
          <PreviewApplication applicationDefinition={applicationDefinition} />
        </Route>
        <Route path="*">
          <NotFound />
        </Route>
      </Switch>
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ApplicationRouter);
